
const normalizeIsoString = (dateString: string) => {
  return dateString.replace(/(\.\d{3})\d+(Z|[+-]\d{2}:?\d{2})$/, '$1$2')
}

export const formatInTimeZone = (
  dateInput: string | number | Date,
  timeZone: string = 'Europe/Warsaw',
  locale: string = 'pl-PL',
  options: Intl.DateTimeFormatOptions = {}
) => {
  const normalizedInput =
    typeof dateInput === 'string' ? normalizeIsoString(dateInput) : dateInput
  const date = new Date(normalizedInput)

  if (Number.isNaN(date.getTime())) {
    return '---'
  }

  return new Intl.DateTimeFormat(locale, { timeZone, ...options }).format(date)
}

// DD.MM.YYYY
export const formatDateWarsaw = (dateInput: string | number | Date) =>
  formatInTimeZone(dateInput, 'Europe/Warsaw', 'pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })

// HH:MM
export const formatTimeWarsaw = (dateInput: string | number | Date) =>
  formatInTimeZone(dateInput, 'Europe/Warsaw', 'pl-PL', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })

// DD.MM.YYYY HH:MM
export const formatDateTimeWarsaw = (dateInput: string | number | Date) =>
  formatInTimeZone(dateInput, 'Europe/Warsaw', 'pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })

// DD.MM.YYYY
export const parseDateToDDMMYYYY = (date?: string) => {
  if (date) {
    return formatDateWarsaw(date)
  } else {
    return '---'
  }
}

// hh:mm
export const getTimeFromDate = (dateString?: string) => {
  if (dateString) {
    const date = new Date(dateString)

    const hour = String(date.getUTCHours())
    const minutes = String(date.getUTCMinutes())

    return `${hour.padStart(2, '0')}:${minutes.padStart(2, '0')}`
  } else {
    return '---'
  }
}

type FormatDatePattern =
  | 'DD/MM/YYYY'
  | 'DD.MM.YYYY'
  | 'D.MM.YYYY'
  | 'D.MM'
  | 'DD.MM'
  | 'DD-MM-YYYY'
  | 'YYYY-MM-DD'

const defaultPattern: FormatDatePattern = 'DD.MM.YYYY'

export const formatDate = (
  dateString: string | null | undefined,
  pattern: FormatDatePattern = defaultPattern
) => {
  if (dateString) {
    const date = new Date(dateString)

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    switch (pattern) {
      case 'DD/MM/YYYY':
        return `${day}-${month}-${year}`
      case 'DD.MM.YYYY':
        return `${day}.${month}.${year}`
      case 'DD-MM-YYYY':
        return `${day}-${month}-${year}`
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`
      case 'D.MM.YYYY':
        return `${String(date.getDate())}.${month}.${year}`
      case 'D.MM':
        return `${String(date.getDate())}.${month}`
      case 'DD.MM':
        return `${day}.${month}`
      default:
        return `${day}.${month}.${year}`
    }
  } else {
    return '---'
  }
}

export const getNumberOfDayFromDate = (date: string) => new Date(date).getDay()

export const formatToSimpleDate = (date: string) => {
  const checkedDate = new Date(date)

  const months = [
    'stycznia',
    'lutego',
    'marca',
    'kwietnia',
    'maja',
    'czerwca',
    'lipca',
    'sierpnia',
    'września',
    'października',
    'listopada',
    'grudnia'
  ]

  const day = checkedDate.getDate()
  const month = checkedDate.getMonth()

  return day + ' ' + months[month]
}

export const formatDateRange = (
  fromDateString: string,
  toDateString: string
): string => {
  const fromDate = new Date(fromDateString)
  const toDate = new Date(toDateString)

  const fromYear = fromDate.getFullYear()

  const toYear = toDate.getFullYear()

  if (fromYear === toYear) {
    return `${formatDate(fromDateString, 'D.MM')}-${formatDate(toDateString, 'D.MM.YYYY')}`
  }

  return `${formatDate(fromDateString, 'D.MM.YYYY')}-${formatDate(toDateString, 'D.MM.YYYY')}`
}

export const validateDateOfBirth = (value?: string) => {
  if (value) {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/

    if (!dateRegex.test(value)) {
      return false // Invalid format
    }

    const dobDate = new Date(value)
    const today = new Date()
    const minDate = new Date()

    minDate.setFullYear(today.getFullYear() - 100)

    if (dobDate > today || dobDate < minDate) {
      return false
    }

    return true
  }

  return false
}

export const getRemainingPromotionTime = (promoEndDate: string | null) => {
  if (!promoEndDate) {
    return null
  }

  const currentTime = new Date()
  const promoEndTime = new Date(promoEndDate)

  const timeDifference = promoEndTime.getTime() - currentTime.getTime()

  if (timeDifference <= 0) {
    return null
  }

  const hours = Math.floor(timeDifference / (1000 * 60 * 60))
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000)

  return { hours, minutes, seconds }
}

export const replaceDateSnakeCaseToDash = (date: string) => {
  return date.replace(/_/g, '-')
}

export const replaceDateDashToSnakeCase = (date: string) => {
  return date.replace(/-/g, '_')
}

export const addDays = (date: Date, days: number) => {
  const result = new Date(date)

  result.setDate(result.getDate() + days)

  return result
}

/**
 * Returns true if the given date falls within [from, to] (inclusive on both ends).
 * Either bound can be omitted — if `from` is absent the lower bound is unchecked,
 * if `to` is absent the upper bound is unchecked.
 *
 * @example
 * // full range check (replaces: new Date(from) <= new Date() && new Date(to) >= new Date())
 * isDateInRange(activeDate?.from, activeDate?.to)
 *
 * @example
 * // only end-date check — has the range already finished?
 * !isDateInRange(undefined, campaignInfo.range.to)
 */

interface RangeDate {
  from?: string | Date | null
  to?: string | Date | null
}

export const isDateInRange = (
  { from, to }: RangeDate,
  date: Date | string = new Date()
): boolean => {
  const fromDate = from
    ? typeof from === 'string'
      ? new Date(from)
      : from
    : null
  const toDate = to ? (typeof to === 'string' ? new Date(to) : to) : null

  const dateToCheck = typeof date === 'string' ? new Date(date) : date

  if (fromDate && fromDate > dateToCheck) return false
  if (toDate && toDate < dateToCheck) return false

  return true
}

export const mapDaysOfWeek = {
  monday: 'Poniedziałek',
  tuesday: 'Wtorek',
  wednesday: 'Środa',
  thursday: 'Czwartek',
  friday: 'Piątek',
  saturday: 'Sobota',
  sunday: 'Niedziela'
}

export const formatSecondsAsMmSs = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`
}
